// src/contexts/NotificationContext.jsx
import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { getUnreadNotificationsCountByUser } from "./../services/notificationServices";
import { useUser } from "./../contexts/UserContext";
import Notification from "./../components/Notification/Notification";

const NotificationContext = createContext({
  unreadCount: 0,
  setUnreadCount: () => {},
  refresh: () => {},
  addNotification: () => {},
  removeNotification: () => {},
});

export const NotificationProvider = ({ children, pollMs = 60000 }) => {
  const { user } = useUser();

  const [unreadCount, setUnreadCount] = useState(0);
  const [toastNotifications, setToastNotifications] = useState([]);

  const intervalRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user || !user.userId) {
      setUnreadCount(0);
      return;
    }

    try {
      const count = await getUnreadNotificationsCountByUser();
      setUnreadCount(Number(count || 0));
    } catch (error) {
      console.error("Erreur refresh notifications :", error);
    }
  }, [user]);

  const removeNotification = useCallback((id) => {
    setToastNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  }, []);

  const addNotification = useCallback((message, type = "success") => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    setToastNotifications((prev) => [
      ...prev,
      {
        id,
        message,
        type,
      },
    ]);

    return id;
  }, []);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (user && user.userId) {
      refresh();
      intervalRef.current = setInterval(refresh, pollMs);

      const onVisibility = () => {
        if (document.visibilityState === "visible") {
          refresh();
        }
      };

      const onFocus = () => {
        refresh();
      };

      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("focus", onFocus);

      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("focus", onFocus);
      };
    }

    setUnreadCount(0);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, pollMs, refresh]);

  const value = useMemo(
    () => ({
      unreadCount,
      setUnreadCount,
      refresh,
      addNotification,
      removeNotification,
    }),
    [unreadCount, refresh, addNotification, removeNotification]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}

      <div className="fixed right-4 top-20 z-[9999] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-3">
        {toastNotifications.map((notification) => (
          <Notification
            key={notification.id}
            message={notification.message}
            type={notification.type}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);